import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantToolResult } from '@cumpli/shared';
import { api, RequestError } from '../../lib/api';
import { useMe } from '../../lib/hooks';
import { useToast } from '../../lib/use-toast';

/** Single-line, usted-form confirmation shown as a toast after a write executes. */
const EXECUTE_TOAST: Record<string, string> = {
  create_expediente: 'Expediente creado',
  update_expediente: 'Expediente actualizado',
  request_documents: 'Documentos solicitados',
  update_alert: 'Alerta actualizada',
  run_validation: 'Validaciones ejecutadas',
  create_review: 'Revisión programada',
  update_risk_matrix: 'Matriz guardada',
  update_indicator_bands: 'Indicadores actualizados',
  update_entity_mode: 'Tipo de entidad actualizado',
};

/**
 * Cumpli AI — multi-conversation state, backed by a constrained tool set
 * (POST /api/v1/assistant/chat). Read tools answer inline; write tools are
 * surfaced as **confirm cards** (propose → confirm → execute): the model can
 * only *propose* an action, and nothing runs until the user confirms it, which
 * calls POST /api/v1/assistant/execute. Knows the client you're viewing (route)
 * so "este cliente" resolves.
 *
 * The assistant is now a THREADED workspace: a list of conversations with one
 * active at a time. Everything — the transcript per thread, thinking flag,
 * ask/confirm/cancel, TanStack invalidation, localStorage persistence, and the
 * new/switch/delete controls — lives in this single provider (mounted once
 * app-wide). Both surfaces (the /agente page and the ⌘K drawer) render the
 * ACTIVE conversation through the shared <Thread>, so they are literally ONE
 * chat: whatever you type in one shows in the other. Surface-UI state (the open
 * flag, the input box, scroll position) stays in each surface.
 *
 * Each conversation's `id` IS the AI decision-log `conversationId`: it is minted
 * client-side when the conversation is created and sent on every ask()/execute,
 * so switching threads correctly re-keys the server-side decision log. See the
 * "AI decision log" concept note.
 */

export type ActionState = 'pending' | 'executing' | 'executed' | 'cancelled' | 'error';

/** A proposed action plus the resolved client-side state of its confirm card. */
export interface ProposedActionUI {
  tool: string;
  title: string;
  summary: string;
  input: Record<string, unknown>;
  state: ActionState;
  /** API error message (es-PA) when state === 'error'. */
  errorMessage?: string;
  /** Row the action created/affected, for deep-linking after execute. */
  entity?: { type: string; id: string };
  /** Restored from a prior session — can't be re-armed; render disabled. */
  expired?: boolean;
}

export interface Msg {
  role: 'user' | 'ai';
  text: string;
  tools?: string[];
  error?: boolean;
  /** Copilot-proposed write actions attached to an 'ai' turn. */
  actions?: ProposedActionUI[];
  /** Rich, redaction-aware read-tool outputs rendered as answer cards under an
   * 'ai' turn. Absent on old transcripts and on turns with no structured data. */
  toolResults?: AssistantToolResult[];
  /** Creation time (ms) — used to expire stale proposals after a reload. */
  ts?: number;
  /** Server-assigned id of this AI turn (B3). A proposal attached here confirms
   * with this as `turnId`, linking the executed action back to the turn that
   * proposed it in the AI decision log. Absent on old transcripts / degraded turns. */
  turnId?: string;
}

/** One conversation thread. `id` is the decision-log conversationId (see file docs). */
export interface Conversation {
  id: string;
  /** Derived from the first user message (truncated), else "Nueva conversación". */
  title: string;
  messages: Msg[];
  createdAt: number;
  updatedAt: number;
}

/** Lightweight per-conversation view for the conversations panel (no messages). */
export interface ConversationSummary {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messageCount: number;
}

/** Persisted shape (v2 = multi-conversation). Older shapes are migrated on load. */
interface StoredStateV2 {
  v: 2;
  /** Top-level TTL stamp — kept for `sweepExpiredConversations` + the load TTL check. */
  ts: number;
  conversations: Conversation[];
  activeId: string | null;
}

/** Legacy v1: a single transcript. Migrated into a one-element list on first load. */
interface StoredConversationV1 {
  ts: number;
  msgs: Msg[];
  conversationId?: string;
}

const STORAGE_PREFIX = 'cumpli:assistant:';
/** 7-day TTL: stale conversations are cleared on shared/shared-device scenarios. */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Backoff delays for HTTP 429. Total max wait before final failure: 2 + 4 + 8 = 14 s. */
const RETRY_DELAYS_MS = [2000, 4000, 8000] as const;
/** Keep at most this many conversations (most-recently-updated win). */
const CONV_CAP = 20;
/** Keep at most this many messages per conversation. */
const MSG_CAP = 30;
/** Title length cap for the conversations panel. */
const TITLE_MAX = 40;
const DEFAULT_TITLE = 'Nueva conversación';

/** Remove all assistant conversations older than TTL_MS to prevent accumulation on shared devices. */
function sweepExpiredConversations(): void {
  try {
    const now = Date.now();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) { keysToRemove.push(key); continue; }
        const parsed = JSON.parse(raw) as { ts?: number };
        if (!parsed.ts || now - parsed.ts > TTL_MS) keysToRemove.push(key);
      } catch {
        keysToRemove.push(key); // corrupt entry — sweep it
      }
    }
    for (const key of keysToRemove) localStorage.removeItem(key);
  } catch {
    // localStorage may be unavailable (private browsing, storage quota, etc.)
  }
}

/** True when an error is a user/fetch abort (a DOMException or any Error named 'AbortError'). */
function isAbortError(err: unknown): boolean {
  return (err as { name?: string } | null)?.name === 'AbortError';
}

/** The reason an aborted signal carries (a DOMException named 'AbortError'), with a fallback. */
function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Aborted', 'AbortError');
}

/** setTimeout that rejects promptly if the signal aborts mid-wait, instead of waiting out the timer. */
function sleepOrAbort(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortReason(signal));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortReason(signal!));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** A promise that rejects the moment `signal` aborts and otherwise never settles.
 * Racing the request against it makes stop() cancel the client wait promptly even
 * when the underlying call (e.g. the in-memory demo) doesn't itself honor the signal. */
function abortPromise(signal: AbortSignal): Promise<never> {
  return new Promise<never>((_, reject) => {
    if (signal.aborted) {
      reject(abortReason(signal));
      return;
    }
    signal.addEventListener('abort', () => reject(abortReason(signal)), { once: true });
  });
}

/** Retry fn on HTTP 429 with exponential backoff; throws on any other error or after
 * exhausting retries. Abort-aware: a user abort (via `signal`) — before the call, mid
 * flight, or during a backoff sleep — rejects immediately with the AbortError and is
 * never swallowed into a 429 retry. */
async function withRateLimitBackoff<T>(
  fn: () => Promise<T>,
  onRetry?: (attempt: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    if (signal?.aborted) throw abortReason(signal);
    try {
      return signal ? await Promise.race([fn(), abortPromise(signal)]) : await fn();
    } catch (err) {
      // A user abort propagates immediately — never retried as a rate-limit.
      if (isAbortError(err) || signal?.aborted) throw err;
      if (err instanceof RequestError && err.status === 429 && attempt < RETRY_DELAYS_MS.length) {
        onRetry?.(attempt + 1);
        await sleepOrAbort(RETRY_DELAYS_MS[attempt]!, signal);
        continue;
      }
      throw err;
    }
  }
}

/** Terminal action states survive a reload as-is; non-terminal ones expire. */
const TERMINAL_STATES: ReadonlyArray<ActionState> = ['executed', 'cancelled', 'error'];

/** Restore a stored message, expiring any non-terminal (unconfirmed) proposal. */
function reviveMsg(m: Msg): Msg {
  if (!m.actions || m.actions.length === 0) return m;
  return {
    ...m,
    actions: m.actions.map((a) =>
      TERMINAL_STATES.includes(a.state) ? a : { ...a, state: 'pending' as const, expired: true }
    ),
  };
}

/** Revive every message in a restored conversation. */
function reviveConversation(c: Conversation): Conversation {
  return { ...c, messages: c.messages.map(reviveMsg) };
}

/** Title from the first user message (single-spaced, truncated), else the default. */
function deriveTitle(messages: Msg[]): string {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return DEFAULT_TITLE;
  const text = firstUser.text.trim().replace(/\s+/g, ' ');
  if (!text) return DEFAULT_TITLE;
  return text.length > TITLE_MAX ? `${text.slice(0, TITLE_MAX).trimEnd()}…` : text;
}

/** A brand-new, empty conversation with a fresh decision-log id. */
function emptyConversation(): Conversation {
  const now = Date.now();
  return { id: crypto.randomUUID(), title: DEFAULT_TITLE, messages: [], createdAt: now, updatedAt: now };
}

/** Keep only the most-recently-updated CONV_CAP conversations (the active one, just
 * touched, always survives). Order is otherwise preserved so the panel can re-sort. */
function capConversations(list: Conversation[]): Conversation[] {
  if (list.length <= CONV_CAP) return list;
  return [...list].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, CONV_CAP);
}

/** Parse stored localStorage into a normalized conversation list, migrating older
 * shapes (bare `Msg[]`, or v1 `{ ts, msgs, conversationId }`) into the v2 list.
 * Returns null when there's nothing usable (caller then seeds an empty thread). */
function parseStored(raw: string): { conversations: Conversation[]; activeId: string | null } | null {
  const parsed = JSON.parse(raw) as unknown;

  // Legacy-legacy: a bare Msg[] transcript → one conversation.
  if (Array.isArray(parsed)) {
    return migrateSingle(parsed as Msg[], undefined, Date.now());
  }
  if (!parsed || typeof parsed !== 'object') return null;

  // TTL: drop anything past the window (sweep normally handles this first).
  const ts = (parsed as { ts?: number }).ts;
  if (ts && Date.now() - ts > TTL_MS) return null;

  // v2 multi-conversation.
  if ('conversations' in parsed && Array.isArray((parsed as StoredStateV2).conversations)) {
    const state = parsed as StoredStateV2;
    const conversations = state.conversations
      .filter((c): c is Conversation => !!c && typeof c.id === 'string' && Array.isArray(c.messages))
      .map(reviveConversation);
    if (conversations.length === 0) return null;
    const activeId =
      state.activeId && conversations.some((c) => c.id === state.activeId)
        ? state.activeId
        : conversations[0]!.id;
    return { conversations, activeId };
  }

  // v1 single transcript → migrate to one conversation.
  if ('msgs' in parsed && Array.isArray((parsed as StoredConversationV1).msgs)) {
    const v1 = parsed as StoredConversationV1;
    return migrateSingle(v1.msgs, v1.conversationId, v1.ts || Date.now());
  }

  return null;
}

/** Wrap a legacy single transcript in a one-element conversation list (no data loss). */
function migrateSingle(
  msgs: Msg[],
  conversationId: string | undefined,
  ts: number,
): { conversations: Conversation[]; activeId: string | null } {
  const messages = msgs.slice(-MSG_CAP).map(reviveMsg);
  const conv: Conversation = {
    id: conversationId ?? crypto.randomUUID(),
    title: deriveTitle(messages),
    messages,
    createdAt: ts,
    updatedAt: ts,
  };
  return { conversations: [conv], activeId: conv.id };
}

/** Pull the expediente id from the current route (/revision/:id or /expedientes/:id). */
function useCurrentExpedienteId(): string | null {
  const { pathname } = useLocation();
  const m = pathname.match(/\/(?:revision|expedientes)\/([^/]+)/);
  return m && m[1] && m[1] !== 'nuevo' ? m[1] : null;
}

interface AssistantChatValue {
  /** Messages of the ACTIVE conversation (what both surfaces render). */
  msgs: Msg[];
  thinking: boolean;
  retryCount: number;
  ask: (text: string) => Promise<void>;
  /** Abort the in-flight ask() (client-side only; see ask() for the server caveat). */
  stop: () => void;
  confirmAction: (msgIdx: number, actionIdx: number) => Promise<void>;
  cancelAction: (msgIdx: number, actionIdx: number) => void;
  /** All conversations (summaries) for the conversations panel, newest activity first. */
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  /** Create a fresh empty conversation and switch to it (no-op if already fresh). */
  newConversation: () => void;
  switchConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
}

const AssistantChatContext = createContext<AssistantChatValue | null>(null);

/** Access the app-wide assistant conversation. Must be used under <AssistantChatProvider>. */
export function useAssistantChat(): AssistantChatValue {
  const ctx = useContext(AssistantChatContext);
  if (!ctx) throw new Error('useAssistantChat must be used within <AssistantChatProvider>');
  return ctx;
}

const EMPTY_MSGS: Msg[] = [];

/**
 * Owns the assistant conversations for the whole app. Mount exactly ONE instance
 * (App root, inside the router + query providers) so the threads survive the
 * drawer opening/closing and route changes.
 */
export function AssistantChatProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [thinking, setThinking] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  // Mirror of activeId for synchronous reads inside ask/confirm/cancel (which run
  // outside render and must target the right thread even mid-flight). Assigned in
  // the render body so it always reflects the latest committed active thread.
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  // The current turn's abort controller — stop() aborts it to cancel the client wait.
  const abortRef = useRef<AbortController | null>(null);

  const expedienteId = useCurrentExpedienteId();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const storageKey = me ? `${STORAGE_PREFIX}${me.tenantId}:${me.id}` : null;

  /** Set the active thread + keep the ref in lockstep for synchronous reads. */
  function selectActive(id: string | null) {
    activeIdRef.current = id;
    setActiveId(id);
  }

  // On first mount, sweep all expired conversations to prevent accumulation on shared devices.
  useEffect(() => {
    sweepExpiredConversations();
  }, []);

  // Restore the saved conversations once we know whose they are (tenant + user).
  // Migrates the legacy single-transcript shapes into a one-element list (no data
  // loss). When storage holds nothing usable, we seed a single empty thread so
  // both surfaces always have an active conversation to render. Any proposal still
  // pending/executing when the app closed is EXPIRED on restore (the model's
  // context is gone) — never re-armed as confirmable. A returning user restores
  // with messages, so the guided-tour autoAsk (which checks msgs.length === 0)
  // won't fire — intended.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      const restored = raw ? parseStored(raw) : null;
      if (restored && restored.conversations.length > 0) {
        setConversations(restored.conversations);
        selectActive(restored.activeId ?? restored.conversations[0]!.id);
        return;
      }
    } catch {
      // Corrupt/unavailable storage — fall through to a fresh thread.
    }
    // Nothing usable in storage: ensure at least one empty conversation, but never
    // stomp on threads a concurrent (StrictMode) run may have already set.
    setConversations((prev) => {
      if (prev.length > 0) return prev;
      const fresh = emptyConversation();
      selectActive(fresh.id);
      return [fresh];
    });
  }, [storageKey]);

  // Persist the whole list + active id (v2), capped, with a TTL stamp. The
  // `conversations.length === 0` guard means we only ever write once at least one
  // thread exists — so the transient pre-hydration empty state can't clobber a
  // stored transcript (that state only exists before the load effect runs).
  useEffect(() => {
    if (!storageKey || conversations.length === 0) return;
    try {
      const capped = capConversations(conversations).map((c) => ({
        ...c,
        messages: c.messages.slice(-MSG_CAP),
      }));
      const stored: StoredStateV2 = {
        v: 2,
        ts: Date.now(),
        conversations: capped,
        activeId,
      };
      localStorage.setItem(storageKey, JSON.stringify(stored));
    } catch {
      // Quota/availability issues are non-fatal for the chat.
    }
  }, [storageKey, conversations, activeId]);

  function newConversation() {
    // Already sitting on a fresh, empty thread → nothing to create.
    const active = conversations.find((c) => c.id === activeId);
    if (active && active.messages.length === 0) return;
    const conv = emptyConversation();
    setConversations((prev) => capConversations([conv, ...prev]));
    selectActive(conv.id);
  }

  function switchConversation(id: string) {
    if (id === activeId) return;
    if (!conversations.some((c) => c.id === id)) return;
    selectActive(id);
  }

  function deleteConversation(id: string) {
    const remaining = conversations.filter((c) => c.id !== id);
    if (remaining.length === 0) {
      const fresh = emptyConversation();
      setConversations([fresh]);
      selectActive(fresh.id);
      return;
    }
    setConversations(remaining);
    if (id === activeId) {
      const mostRecent = [...remaining].sort((a, b) => b.updatedAt - a.updatedAt)[0]!;
      selectActive(mostRecent.id);
    }
  }

  /** Append/transform the messages of one thread, refreshing its title + updatedAt. */
  function patchMessages(convId: string, updater: (msgs: Msg[]) => Msg[]) {
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== convId) return c;
        const messages = updater(c.messages).slice(-MSG_CAP);
        return { ...c, messages, title: deriveTitle(messages), updatedAt: Date.now() };
      })
    );
  }

  /** Patch a single proposal's confirm-card state within a thread. */
  function updateAction(
    convId: string | null,
    msgIdx: number,
    actionIdx: number,
    patch: Partial<ProposedActionUI>
  ) {
    if (!convId) return;
    setConversations((prev) =>
      prev.map((c) => {
        if (c.id !== convId) return c;
        const messages = c.messages.map((m, i) => {
          if (i !== msgIdx || !m.actions) return m;
          return { ...m, actions: m.actions.map((a, j) => (j === actionIdx ? { ...a, ...patch } : a)) };
        });
        return { ...c, messages, updatedAt: Date.now() };
      })
    );
  }

  // Refresh the TanStack caches the executed tool touched, so any open screen
  // (table/detail/docs) reflects the mutation without a manual reload.
  function invalidateForTool(
    tool: string,
    actionInput: Record<string, unknown>,
    entity?: { type: string; id: string }
  ) {
    const inputExpId =
      typeof actionInput.expedienteId === 'string' ? actionInput.expedienteId : undefined;
    const expId = inputExpId ?? (entity?.type === 'expediente' ? entity.id : undefined);
    const inputAlertId =
      typeof actionInput.alertId === 'string' ? actionInput.alertId : undefined;
    const alertId = inputAlertId ?? (entity?.type === 'alert' ? entity.id : undefined);
    const inv = (queryKey: readonly unknown[]) => void qc.invalidateQueries({ queryKey });
    switch (tool) {
      case 'create_expediente':
      case 'update_expediente':
        inv(['expedientes']);
        if (expId) inv(['expedientes', expId]);
        break;
      case 'request_documents':
        inv(['expedientes']);
        if (expId) inv(['expedientes', expId]);
        inv(['all-documents']);
        break;
      case 'run_validation':
        inv(['expedientes']);
        if (expId) inv(['expedientes', expId]);
        break;
      case 'update_alert':
        inv(['alertas']);
        if (alertId) inv(['alertas', alertId]);
        break;
      case 'create_review':
        inv(['expedientes']);
        if (expId) inv(['expedientes', expId]);
        break;
      case 'update_risk_matrix':
      case 'update_indicator_bands':
        inv(['risk-matrix']);
        break;
      case 'update_entity_mode':
        inv(['tenant-settings']);
        inv(['risk-matrix']);
        break;
      default:
        break;
    }
  }

  async function confirmAction(msgIdx: number, actionIdx: number) {
    const targetId = activeIdRef.current;
    const conv = conversations.find((c) => c.id === targetId);
    const msg = conv?.messages[msgIdx];
    const action = msg?.actions?.[actionIdx];
    if (!action || action.state !== 'pending' || action.expired) return;
    updateAction(targetId, msgIdx, actionIdx, { state: 'executing' });
    try {
      // Link the confirmation to the proposal in the AI decision log (B3):
      // conversationId (the thread id) + the AI turn's turnId. `edited: false` is
      // honest — the confirm card is confirm/cancel only (no edit-before-confirm
      // flow), so the user accepted the proposal as-is.
      const res = await api.assistantExecute(action.tool, action.input, {
        ...(targetId ? { conversationId: targetId } : {}),
        ...(msg.turnId ? { turnId: msg.turnId } : {}),
        edited: false,
      });
      updateAction(targetId, msgIdx, actionIdx, {
        state: 'executed',
        ...(res.entity ? { entity: res.entity } : {}),
      });
      invalidateForTool(action.tool, action.input, res.entity);
      toast({ message: EXECUTE_TOAST[action.tool] ?? 'Acción ejecutada', kind: 'success' });
    } catch (err) {
      const message =
        err instanceof RequestError
          ? err.message
          : 'No se pudo ejecutar la acción. Inténtalo de nuevo.';
      updateAction(targetId, msgIdx, actionIdx, { state: 'error', errorMessage: message });
    }
  }

  function cancelAction(msgIdx: number, actionIdx: number) {
    updateAction(activeIdRef.current, msgIdx, actionIdx, { state: 'cancelled' });
  }

  async function ask(text: string) {
    const msg = text.trim();
    if (!msg || thinking) return;

    // Resolve the target thread. If none is active (fresh user, or the active id
    // drifted), create one and switch to it — the ask lands there, and every
    // continuation below targets this captured id even if the user switches away.
    let targetId = activeIdRef.current;
    if (!targetId || !conversations.some((c) => c.id === targetId)) {
      const conv = emptyConversation();
      targetId = conv.id;
      setConversations((prev) => capConversations([conv, ...prev]));
      selectActive(conv.id);
    }
    const convId = targetId;

    // Build conversational history from the target thread's current snapshot
    // (excludes the message we're about to send), mapping 'ai' → the API's 'assistant'.
    const current = conversations.find((c) => c.id === convId)?.messages ?? [];
    const history = current
      .slice(-8)
      .map((m) => ({
        role: m.role === 'ai' ? ('assistant' as const) : ('user' as const),
        text: m.text.slice(0, 4000),
      }));

    patchMessages(convId, (m) => [...m, { role: 'user', text: msg, ts: Date.now() }]);
    setThinking(true);
    setRetryCount(0);
    // Fresh abort controller for this turn; stop() aborts it.
    // NOTE: abort only cancels the CLIENT's wait. The POST has already reached the
    // server, so the turn may still be logged and counts toward the rate/token
    // budget — we intentionally do NOT attempt a server-side cancel.
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const r = await withRateLimitBackoff(
        () =>
          api.assistantChat(
            msg,
            expedienteId ?? undefined,
            history.length > 0 ? history : undefined,
            // The thread id IS the decision-log conversationId — keep it stable so
            // the server groups this turn under the right thread.
            convId,
            controller.signal,
          ),
        (attempt) => setRetryCount(attempt),
        controller.signal,
      );
      const actions: ProposedActionUI[] = (r.proposedActions ?? []).map((a) => ({
        tool: a.tool,
        title: a.title,
        summary: a.summary,
        input: a.input,
        state: 'pending',
      }));
      patchMessages(convId, (m) => [
        ...m,
        {
          role: 'ai',
          text: r.answer,
          tools: r.toolsUsed,
          ts: Date.now(),
          ...(actions.length > 0 ? { actions } : {}),
          ...(r.toolResults && r.toolResults.length > 0 ? { toolResults: r.toolResults } : {}),
          ...(r.turnId ? { turnId: r.turnId } : {}),
        },
      ]);
    } catch (e) {
      // A user-initiated stop() aborts the in-flight fetch. Don't render the generic
      // red error — leave a muted, NON-error note so the user's message isn't left
      // dangling unanswered (their message stays in the transcript as-is).
      if ((e as Error | null)?.name === 'AbortError') {
        patchMessages(convId, (m) => [
          ...m,
          { role: 'ai', text: 'Cancelaste esta consulta.', ts: Date.now() },
        ]);
      } else {
        const errorText =
          e instanceof RequestError && e.status === 401
            ? 'Tu sesión ha expirado. Recarga la página e ingresa de nuevo.'
            : e instanceof RequestError && e.status === 429
            ? 'Demasiadas consultas al asistente. Espera un momento e inténtalo de nuevo.'
            : 'No pude responder en este momento. Inténtalo de nuevo.';
        patchMessages(convId, (m) => [...m, { role: 'ai', text: errorText, error: true, ts: Date.now() }]);
      }
    } finally {
      setThinking(false);
      setRetryCount(0);
      abortRef.current = null;
    }
  }

  /** Cancel the in-flight ask() by aborting its request (client-side only). */
  function stop() {
    abortRef.current?.abort();
  }

  const activeMsgs = conversations.find((c) => c.id === activeId)?.messages ?? EMPTY_MSGS;

  const summaries = useMemo<ConversationSummary[]>(
    () =>
      conversations.map((c) => ({
        id: c.id,
        title: c.title,
        createdAt: c.createdAt,
        updatedAt: c.updatedAt,
        messageCount: c.messages.length,
      })),
    [conversations]
  );

  const value: AssistantChatValue = {
    msgs: activeMsgs,
    thinking,
    retryCount,
    ask,
    stop,
    confirmAction,
    cancelAction,
    conversations: summaries,
    activeConversationId: activeId,
    newConversation,
    switchConversation,
    deleteConversation,
  };

  return <AssistantChatContext.Provider value={value}>{children}</AssistantChatContext.Provider>;
}
